/**
 * MoodMate date helpers — all in the user's LOCAL timezone.
 *
 * Records are stored as ISO instants (createdAt). Everything a human sees — which
 * day a check-in belongs to, which week, which month — is computed from the local
 * calendar via the native Date, so day boundaries, month lengths and leap years
 * are correct without a date library. A check-in at 23:30 local belongs to that
 * local day even though its ISO string may be the next day in UTC.
 */

/** Local midnight for a date (strips the time, in local tz). */
export const localMidnight = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** 'YYYY-MM-DD' in LOCAL time — the stable key for "which day". */
export const dayKey = (d: Date): string => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

export const addDays = (d: Date, n: number): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const addMonths = (d: Date, n: number): Date => new Date(d.getFullYear(), d.getMonth() + n, 1);

/** Monday as the first day of the week. */
export const startOfWeek = (d: Date): Date => {
  const base = localMidnight(d);
  const offset = (base.getDay() + 6) % 7; // 0 = Mon … 6 = Sun
  return addDays(base, -offset);
};

/** The seven local dates of the week containing `d`, Monday → Sunday. */
export const weekDays = (d: Date): Date[] => {
  const start = startOfWeek(d);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
};

/** A stable key for a week (its Monday's dayKey) — used to file weekly reflections. */
export const weekKey = (d: Date): string => dayKey(startOfWeek(d));

/** 'YYYY-MM' — used to file monthly reflections. */
export const monthKey = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

export const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);

/**
 * A stable 6×7 calendar grid (42 cells) for the month containing `d`, starting on
 * Monday, with leading/trailing days from the neighbouring months marked.
 */
export interface GridDay {
  date: Date;
  key: string;
  inMonth: boolean;
  isToday: boolean;
}
export const monthGrid = (d: Date, today: Date = new Date()): GridDay[] => {
  const first = startOfMonth(d);
  const gridStart = startOfWeek(first);
  const todayKey = dayKey(today);
  return Array.from({ length: 42 }, (_, i) => {
    const date = addDays(gridStart, i);
    return {
      date,
      key: dayKey(date),
      inMonth: date.getMonth() === d.getMonth(),
      isToday: dayKey(date) === todayKey,
    };
  });
};

export const isSameDay = (a: Date, b: Date): boolean => dayKey(a) === dayKey(b);

// ── Formatting ───────────────────────────────────────────────────────────────
export const fmtWeekday = (d: Date): string => d.toLocaleDateString(undefined, { weekday: 'short' });
export const fmtDayNum = (d: Date): string => String(d.getDate());
export const fmtTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
export const fmtLongDay = (d: Date): string =>
  d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
export const fmtMonthYear = (d: Date): string =>
  d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
export const fmtWeekRange = (d: Date): string => {
  const start = startOfWeek(d);
  const end = addDays(start, 6);
  const sameMonth = start.getMonth() === end.getMonth();
  const startStr = start.toLocaleDateString(undefined, { day: 'numeric', month: sameMonth ? undefined : 'short' });
  const endStr = end.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${startStr} – ${endStr}`;
};
